import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Logo from "../components/Logo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../lib/auth-context";
import { useI18n } from "../lib/i18n";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";

const DEMO_ACCOUNTS = [
    { label: "Consumer (Amina)", email: "amina@onepass.demo", role: "consumer" },
    { label: "Consumer (Michael)", email: "michael@onepass.demo", role: "consumer-michael" },
    { label: "Business (John, Bright Foods)", email: "john@brightfoods.demo", role: "business" },
    { label: "Business (Grace, Grace Ent.)", email: "grace@graceent.demo", role: "business-grace" },
    { label: "Admin (Adaeze)", email: "admin@onepass.demo", role: "admin" },
];

export default function LoginPage() {
    const nav = useNavigate();
    const loc = useLocation();
    const { login } = useAuth();
    const { t } = useI18n();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [show, setShow] = useState(false);
    const [busy, setBusy] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const u = await login(email, password);
            toast.success(`Welcome back, ${u.full_name.split(" ")[0]}!`);
            const home = u.role === "admin" ? "/admin" : u.role === "business" ? "/business" : "/consumer";
            const target = loc.state?.from?.pathname || home;
            nav(target, { replace: true });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Login failed");
        } finally {
            setBusy(false);
        }
    };

    const fillDemo = (e) => {
        setEmail(e);
        setPassword("demo1234");
    };

    return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
            <div className="w-full max-w-md">
                <div className="text-center mb-6">
                    <Logo size={48} asLink />
                </div>
                <div className="bg-white rounded-3xl p-8 border border-slate-200 product-shadow" data-testid="login-card">
                    <h1 className="font-display text-2xl font-semibold text-[#0A1930]">Welcome back</h1>
                    <p className="text-sm text-slate-500 mt-1">Sign in to continue to OnePassCredit</p>

                    <form onSubmit={onSubmit} className="mt-6 space-y-4">
                        <div>
                            <Label htmlFor="email" className="text-sm font-medium">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@email.com"
                                className="mt-1.5 h-11 rounded-xl"
                                required
                                data-testid="login-email-input"
                            />
                        </div>
                        <div>
                            <Label htmlFor="password" className="text-sm font-medium">{t("common.password")}</Label>
                            <div className="relative mt-1.5">
                                <Input
                                    id="password"
                                    type={show ? "text" : "password"}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="••••••••"
                                    className="h-11 rounded-xl pr-10"
                                    required
                                    data-testid="login-password-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShow((s) => !s)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                                    data-testid="login-toggle-password"
                                >
                                    {show ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                        </div>
                        <Button
                            type="submit"
                            disabled={busy}
                            className="w-full h-11 rounded-xl bg-[#0A1930] hover:bg-[#0d1f3d] font-semibold"
                            data-testid="login-submit-btn"
                        >
                            {busy ? t("common.processing") : t("common.signin")}
                        </Button>
                    </form>

                    <div className="mt-6 pt-6 border-t border-slate-100">
                        <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-3">
                            {t("common.demo_accounts")} (password: demo1234)
                        </div>
                        <div className="space-y-2">
                            {DEMO_ACCOUNTS.map((a) => (
                                <button
                                    key={a.email}
                                    onClick={() => fillDemo(a.email)}
                                    type="button"
                                    data-testid={`demo-fill-${a.role}`}
                                    className="w-full text-left text-xs px-3 py-2 rounded-lg bg-slate-50 hover:bg-slate-100 transition border border-slate-200"
                                >
                                    <span className="font-semibold">{a.label}</span>
                                    <span className="text-slate-500 ml-2 font-mono">{a.email}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    <p className="text-center text-sm text-slate-500 mt-6">
                        {t("auth.new_to_onepass")}{" "}
                        <Link to="/register" className="text-[#0066FF] font-semibold" data-testid="login-go-register">
                            {t("common.create_account")}
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
